import { useRouteContext } from '@tanstack/react-router'

import { type CustomEventPayloadRecord, customEvents } from '@/integrations/insights/custom-events'

import type { InsightsApi } from '@insights/insights-data-provider'
import type IContext from '@insights/insights-data-provider/lib/types/IContext'

export type EventName = keyof CustomEventPayloadRecord

type KeysWithPayload = {
  [K in EventName]: CustomEventPayloadRecord[K] extends never | undefined ? never : K
}[EventName]

export type SendOptions<K extends EventName> = K extends KeysWithPayload
  ? { context?: IContext; payload: CustomEventPayloadRecord[K] }
  : { context?: IContext }

export function useInsights() {
  const { insightsApi: _insightsApi } = useRouteContext({ from: '__root__' })

  async function sendCustomEvent<K extends EventName>(name: K, options?: SendOptions<K>) {
    const category = customEvents[name]
    await _insightsApi?.sendEvent({ category, event: name, ...options })
  }

  // Hide sendEvent to avoid misuse
  const insightsApi = _insightsApi as null | Omit<InsightsApi, 'sendEvent'>

  return { insightsApi, sendCustomEvent }
}
